import { Injectable } from '@angular/core';
import { VideoCompressionService } from './video-compression.service';
import {
  VIDEO_MAX_SOURCE_MB,
  VIDEO_SERVER_UPLOAD_LIMIT_MB,
  VIDEO_UPLOAD_TARGET_MB,
} from './video-upload.constants';

const VIDEO_EXTENSIONS = new Set([
  'mp4', 'mov', 'wmv', 'flv', 'avchd', 'webm', 'mkv', 'vob', 'ogv', 'gifv',
  'm4p', 'm4v', '3gp', '3g2', 'amv', 'nsv', 'ogg', 'f4v', 'f4p', 'f4a', 'f4b',
  'avi', 'mts', 'm2ts', 'ts', 'mpg', 'mpeg', 'rm', 'rmvb', 'divx', 'xvid',
  'asf', 'mxf', 'dv', 'h264', 'h265', 'vp8', 'vp9', 'prores', 'swf', 'dat',
  'bik', 'av1', 'vro', 'svi',
]);

@Injectable({
  providedIn: 'root',
})
export class MediaUploadService {
  readonly serverLimitMb = VIDEO_SERVER_UPLOAD_LIMIT_MB;
  readonly uploadTargetMb = VIDEO_UPLOAD_TARGET_MB;
  readonly maxSourceVideoMb = VIDEO_MAX_SOURCE_MB;

  constructor(private readonly videoCompressionService: VideoCompressionService) {}

  isVideoFile(file: File): boolean {
    if (this.videoCompressionService.isVideoFile(file)) {
      return true;
    }
    return this.isVideoFileName(file.name);
  }

  isVideoFileName(fileName: string): boolean {
    const ext = (fileName.split('.').pop() || '').toLowerCase();
    return VIDEO_EXTENSIONS.has(ext);
  }

  /**
   * Optimizes large videos in the browser so they fit the server upload limit.
   * Non-video files are returned unchanged.
   */
  async prepareFileForUpload(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<File> {
    if (!this.isVideoFile(file)) {
      return file;
    }

    const maxSourceBytes = this.maxSourceVideoMb * 1024 * 1024;
    if (file.size > maxSourceBytes) {
      throw new Error(
        `Video is too large (${this.formatMb(file.size)}MB). Maximum source size is ${this.maxSourceVideoMb}MB.`
      );
    }

    const uploadLimitBytes = VIDEO_UPLOAD_TARGET_MB * 1024 * 1024;
    if (
      file.size <= uploadLimitBytes &&
      !this.videoCompressionService.needsCompression(file)
    ) {
      return file;
    }

    const compressed = await this.videoCompressionService.compressVideo(file, onProgress);
    if (compressed.size > uploadLimitBytes) {
      throw new Error(
        `Video could not be optimized below ${VIDEO_UPLOAD_TARGET_MB}MB (result: ${this.formatMb(compressed.size)}MB). Try a shorter clip or lower resolution source.`
      );
    }

    return compressed;
  }

  formatMb(bytes: number): string {
    return (bytes / 1024 / 1024).toFixed(1);
  }

  getHttp413Message(fileSizeBytes: number): string {
    const mb = this.formatMb(fileSizeBytes);
    return `Server rejected the upload (${mb}MB). Deploy the latest API update (raises the 30MB platform limit to 80MB), or retry with a shorter video.`;
  }

  uploadTooLargeMessage(): string {
    return `Upload failed. Videos up to ${this.maxSourceVideoMb}MB are optimized automatically before upload (target under ${VIDEO_UPLOAD_TARGET_MB}MB).`;
  }
}
